import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Loader2, Sparkles, MapPin, Tag, Layers } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

const REGIONS = [
  { code: "DE", label: "Almanya (DE)" },
  { code: "AT", label: "Avusturya (AT)" },
  { code: "CH", label: "İsviçre (CH)" },
  { code: "TR", label: "Türkiye (TR)" },
  { code: "NL", label: "Hollanda (NL)" },
];

const MAX_OPTS = [20, 40, 60, 100, 200];

const splitMulti = (s) =>
  (s || "")
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);

export const SearchPanel = ({ onSearch, loading }) => {
  const [mode, setMode] = useState("text");
  const [bulk, setBulk] = useState(false);
  const [query, setQuery] = useState("");
  const [queriesText, setQueriesText] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("DE");
  const [maxResults, setMaxResults] = useState("60");

  const cities = splitMulti(city);
  const categories = splitMulti(category);
  const queries = splitMulti(queriesText);

  const canSubmit = (() => {
    if (mode === "text") {
      return bulk ? queries.length > 0 : query.trim().length >= 2;
    }
    return cities.length > 0 && categories.length > 0;
  })();

  const comboCount =
    mode === "text"
      ? bulk
        ? queries.length
        : 1
      : cities.length * categories.length;

  const submit = () => {
    if (!canSubmit || loading) return;
    const payload = {
      mode,
      region_code: region,
      language_code: region === "TR" ? "tr" : "de",
      max_results: parseInt(maxResults, 10),
    };
    if (mode === "text") {
      if (bulk) payload.queries = queries;
      else payload.query = query.trim();
    } else {
      payload.cities = cities;
      payload.categories = categories;
    }
    onSearch(payload);
  };

  const clear = () => {
    setQuery("");
    setQueriesText("");
    setCity("");
    setCategory("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && mode === "text" && !bulk) submit();
  };

  return (
    <Card className="shadow-[var(--shadow-card)] border-border/80">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-primary" />
          İşletme Ara
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={mode} onValueChange={setMode} data-testid={TESTIDS.searchModeTabs}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="text" data-testid="tab-text">
              Serbest Metin
            </TabsTrigger>
            <TabsTrigger value="city_category" data-testid="tab-city-category">
              Şehir + Kategori
            </TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="mt-4 space-y-2">
            {bulk ? (
              <>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Sorgular (her satıra bir tane)
                </Label>
                <Textarea
                  data-testid="bulk-queries-input"
                  placeholder={"Berlin lastik tamircisi\nHamburg kuaför\nMünih restoran"}
                  value={queriesText}
                  onChange={(e) => setQueriesText(e.target.value)}
                  rows={4}
                />
              </>
            ) : (
              <>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Serbest Metin
                </Label>
                <Input
                  data-testid={TESTIDS.freeTextInput}
                  placeholder="Örn: Berlin lastik tamircisi"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="city_category" className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> Şehir{bulk ? "ler (virgülle ayırın)" : ""}
              </Label>
              <Input
                data-testid={TESTIDS.cityInput}
                placeholder={bulk ? "Berlin, Hamburg, Münih" : "Örn: Berlin"}
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Tag className="h-3.5 w-3.5" /> Kategori{bulk ? "ler (virgülle ayırın)" : ""}
              </Label>
              <Input
                data-testid={TESTIDS.categoryInput}
                placeholder={bulk ? "Kuaför, Restoran, Lastikçi" : "Örn: Lastikçi, Kuaför"}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Bulk toggle */}
        <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            <div>
              <Label htmlFor="bulk-switch" className="text-sm cursor-pointer">
                Toplu Arama
              </Label>
              <p className="text-xs text-muted-foreground">Çoklu şehir/kategori tek listede</p>
            </div>
          </div>
          <Switch
            id="bulk-switch"
            checked={bulk}
            onCheckedChange={setBulk}
            data-testid="bulk-switch"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Bölge
            </Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid={TESTIDS.regionSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Maks. Sonuç {bulk ? "(sorgu başına)" : ""}
            </Label>
            <Select value={maxResults} onValueChange={setMaxResults}>
              <SelectTrigger data-testid={TESTIDS.maxResultsSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MAX_OPTS.map((o) => (
                  <SelectItem key={o} value={String(o)}>
                    {o} sonuç
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {comboCount > 1 && (
          <p className="text-xs text-primary font-medium">
            {comboCount} sorgu çalıştırılacak · tekrarlar otomatik temizlenir
          </p>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button
            className="flex-1"
            onClick={submit}
            disabled={!canSubmit || loading}
            data-testid={TESTIDS.searchSubmit}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Aranıyor…
              </>
            ) : (
              <>
                <Search className="h-4 w-4" /> Ara
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={clear}
            disabled={loading}
            data-testid={TESTIDS.searchClear}
          >
            Temizle
          </Button>
        </div>

        <p className="flex items-start gap-1.5 text-xs text-muted-foreground leading-relaxed">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--lead-fg))]" />
          İpucu: Web sitesi olmayan işletmeler turuncu <strong>LEAD</strong> rozetiyle işaretlenir — bunlar senin potansiyel müşterilerin.
        </p>
      </CardContent>
    </Card>
  );
};
